<template>
  <LoadingSpinner :loading="loading"></LoadingSpinner>
  <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>

  <div class="container mt-4 mb-4">
    <div style="max-width:640px">
      <h3>Eget domene</h3>
      <div style="font-style:italic; color:var(--bo-muted)" class="mb-4">
        Bestillingssiden din ligger alltid på <code>{{ platformHost }}</code>. Vil du heller bruke ditt
        eget domene, legger du det inn her. Begge adressene fortsetter å virke, og gjester som kommer inn
        på <code>{{ platformHost }}</code> blir sendt videre til ditt eget domene.
      </div>

      <!-- Status first. Someone opening this page while waiting wants to know where it is stuck, and a
           screen that says nothing is what produces a phone call. -->
      <div v-if="state.host" class="cd-card mb-4" :class="'cd-card--' + statusKind">
        <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap:0.5rem">
          <span class="fw-semibold">{{ displayHost }}</span>
          <span class="badge" :class="badgeClass">{{ statusLabel }}</span>
        </div>
        <!-- A name with æ, ø or å travels the internet as xn--…; some DNS panels and every lookup show that
             form, so say once that it is the same name. -->
        <div v-if="isIdn" class="cd-note mt-1">
          Teknisk navn: <code>{{ state.host }}</code> — samme domene, slik det står i DNS.
        </div>
        <div v-if="state.status === 'PENDING_DNS'" class="mt-3">
          <p class="mb-2">
            Logg inn der du kjøpte domenet — hos Domeneshop, GoDaddy eller hvem det nå er — og finn
            <em>DNS</em>, <em>DNS-pekere</em> eller <em>Nameservers</em>. Legg inn disse to:
          </p>
          <table class="admin-table cd-dns">
            <thead><tr><th>Type</th><th>Navn</th><th>Verdi</th></tr></thead>
            <tbody>
              <tr>
                <td class="mono">A</td>
                <td class="mono">@</td>
                <td class="mono">{{ serverAddress || '—' }}</td>
              </tr>
              <tr>
                <td class="mono">CNAME</td>
                <td class="mono">www</td>
                <td class="mono">{{ state.host }}</td>
              </tr>
            </tbody>
          </table>
          <p class="cd-note mt-2">
            <code>@</code> betyr domenet selv — noen kontrollpaneler vil ha det feltet tomt, eller at du
            skriver <code>{{ displayHost }}</code>. Har du allerede en A-oppføring på <code>@</code>, skal
            den endres, ikke legges til en gang til.
          </p>
          <p class="cd-note mt-2">
            Den andre raden med CNAME gjør at <code>www.{{ displayHost }}</code> også virker. Hopper du over den,
            fungerer alt det andre som normalt.
          </p>
          <p v-if="isIdn" class="cd-note mt-2">
            Verdien i CNAME-raden står med det tekniske navnet, fordi det alltid godtas. Godtar
            kontrollpanelet ditt <code>{{ displayHost }}</code>, går det like bra.
          </p>
          <p class="cd-note mt-2">
            Vi sjekker automatisk og setter opp resten selv når domenet peker hit. En DNS-endring kan ta
            noen timer før den er synlig overalt.
          </p>
        </div>
        <div v-if="state.lastError" class="cd-note mt-2" style="color:#B45309;">{{ state.lastError }}</div>
        <!--
          Two different facts, and only one of them keeps moving.

          While a domain is still being set up, "last checked" is the useful one: it shows the automatic
          check is running and roughly when it last looked. Once the domain is ACTIVE the reconciler skips
          it entirely and never reports again, so lastCheckedEpoch freezes at the moment it went live.
          Showing "sist kontrollert" then is a slow lie — in three months it still reads September, which
          looks like monitoring that stopped rather than a domain with nothing left to check.
        -->
        <div v-if="state.status === 'ACTIVE' && state.activatedEpoch" class="cd-note mt-2">
          Aktiv siden {{ formatWhen(state.activatedEpoch) }}.
        </div>
        <div v-else-if="state.lastCheckedEpoch" class="cd-note mt-2">
          Sist kontrollert {{ formatWhen(state.lastCheckedEpoch) }}.
        </div>
      </div>

      <div class="mt-3">
        <span class="ingredient-title">Domene</span>
        <input class="form-control" type="text" v-model.trim="host" placeholder="f.eks. tastykitchen.no"
               autocomplete="off" @keyup.enter="save" />
        <div class="cd-note mt-2">Uten <code>https://www.</code> — æ, ø og å går fint.</div>
      </div>

      <div v-if="message" class="mt-3">
        <span :class="messageError ? 'message-error' : 'message-info'">{{ message }}</span>
      </div>

      <div class="bo_hr mt-4"><hr /></div>
      <div class="d-flex gap-3 mb-5">
        <button type="button" class="bo_btn" :disabled="saving || !host || host === state.host || host === displayHost" @click="save">
          {{ saving ? 'Lagrer…' : (state.host ? 'Endre domene' : 'Legg til domene') }}
        </button>
        <button v-if="state.host" type="button" class="bo_btn_secondary" :disabled="saving" @click="remove">
          Fjern
        </button>
      </div>

      <!-- SUPER_ADMIN only: the merchant cannot act on any of this, and the data comes from an
           /api/superadmin route that refuses everyone else anyway. -->
      <section v-if="isSuperAdmin && viva" class="cd-card cd-admin mb-5">
        <div class="d-flex align-items-center flex-wrap" style="gap:0.5rem">
          <span class="fw-semibold">VIVA-betalingskilder</span>
          <span class="cd-tag">kun SUPER_ADMIN</span>
        </div>
        <p class="cd-note mt-2 mb-2">
          Etter betaling sender VIVA gjesten tilbake til adressen som står på avdelingens
          <em>betalingskilde</em>. Kilden lages når avdelingen aktiverer VIVA. Gjesten bestiller nå på
          <code>{{ viva.expectedHost }}</code>.
        </p>
        <ol class="cd-note cd-cases mb-3">
          <li>
            <strong>Eget domene var aktivt da VIVA ble aktivert.</strong> Kilden lages med eget domene, så
            gjesten betaler på <code>{{ viva.customDomainActive ? viva.expectedHost : 'eget domene' }}</code>
            og kommer tilbake dit. Ingenting å gjøre.
          </li>
          <li>
            <strong>Eget domene ble aktivt etter at VIVA ble aktivert.</strong> Den gamle kilden sender gjesten
            tilbake til <code>{{ viva.platformHost }}</code>. Handlekurven ligger i nettleserens
            sessionStorage, som er per domene, så gjesten ville kommet tilbake til en nettside som ikke ser
            handlekurven, og bestillingstypen ville vært borte. Derfor lager CAGISTA en <em>ny</em> kilde med
            eget domene for hver avdeling på VIVA i det domenet blir aktivt, og bytter til den. Fjernes eller
            byttes domenet, flyttes kildene tilbake til <code>{{ viva.platformHost }}</code>. Du får SMS og
            e-post om hva som ble flyttet — og om noe ikke lot seg flytte.
            <br />
            <strong>Feilet det,</strong> står avdelingen som «Feil domene» under: trykk «Lag ny kilde». Endre
            aldri adressen på kilden i VIVA self-care, og lag den aldri der — en kilde laget for hånd avviser
            betalingen (<code>eventId=12005</code>). Logo og avbryt-knapp kan endres der (testet 13.10.2026).
          </li>
        </ol>

        <div v-if="!viva.storefrontDomainConfigured" class="cd-warn mb-3">
          <strong>Storefront-domene er tomt</strong> i System → Config → Viva Partner Config. En avdeling som
          aktiverer VIVA nå, får ingen kilde.
        </div>

        <table class="admin-table cd-sources">
          <thead><tr><th>Avdeling</th><th>Kilde</th><th>Returnerer til</th><th>Status</th></tr></thead>
          <tbody>
            <tr v-for="row in viva.locations" :key="row.locationId || row.shortName">
              <td>{{ row.name || row.shortName }}<div class="cd-sub mono">{{ row.shortName }}</div></td>
              <td class="mono">{{ row.sourceCode || '—' }}</td>
              <td>
                <span v-if="row.sourceDomain" class="mono">{{ row.sourceDomain }}</span>
                <span v-else class="cd-sub">{{ row.sourceCode ? 'Ikke registrert' : '—' }}</span>
              </td>
              <td>
                <span class="badge" :class="verdictBadge(row.verdict)">{{ verdictLabel(row.verdict) }}</span>
                <button v-if="row.canMove" type="button" class="bo_btn_secondary cd-move mt-2"
                        :disabled="!!moving" @click="moveSource(row)">
                  {{ moving === row.locationId ? 'Lager…' : 'Lag ny kilde' }}
                </button>
                <!-- A source that points the right way can still refuse every payment: one edited in VIVA self-care
                     sends the guest back with eventId=12005 and no transaction. This replaces it (2026-10-12). -->
                <button v-else-if="row.canReplace" type="button" class="bo_btn_secondary cd-move mt-2"
                        :disabled="!!moving" @click="moveSource(row, true)">
                  {{ moving === row.locationId ? 'Lager…' : 'Lag ny kilde' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="sourceMessage" class="mt-2">
          <span :class="sourceMessageError ? 'message-error' : 'message-info'">{{ sourceMessage }}</span>
        </div>
        <div v-for="row in viva.locations.filter((r) => verdictHelp(r))" :key="'help-' + (row.locationId || row.shortName)"
             class="cd-note mt-2">
          <strong>{{ row.name || row.shortName }}:</strong> {{ verdictHelp(row) }}
        </div>
      </section>
    </div>
  </div>
</template>

<script>
/**
 * Where a merchant points their own domain at their ordering site.
 *
 * The page only records the wish. A reconciler on the storefront box does the DNS check, the certificate
 * and the nginx block, and reports back — so what this screen mostly does is SHOW WHERE IT IS, which is
 * the difference between self-service and a support call.
 *
 * Design: Storefront/docs/custom-domains-implementation.md
 */
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfirmDialogue from "@/components/other/ConfirmDialogue.vue";
import SettingsService from "@/services/settings/SettingsService";
import UserUtil from "@/util/UserUtil";

export default {
  components: { LoadingSpinner, ConfirmDialogue },
  data() {
    return {
      loading: true,
      saving: false,
      host: "",
      state: {},
      serverAddress: "",
      platformHost: "",
      message: "",
      messageError: false,
      viva: null,
      moving: "",
      sourceMessage: "",
      sourceMessageError: false
    };
  },
  computed: {
    isSuperAdmin() {
      return UserUtil.isSuperAdmin();
    },
    /** The name as typed — møllapizzagrill.no. state.host is the xn-- form DNS holds. */
    displayHost() {
      return this.state.hostDisplay || this.state.host || "";
    },
    isIdn() {
      return !!this.state.host && this.displayHost !== this.state.host;
    },
    statusKind() {
      if (this.state.status === "ACTIVE") return "ok";
      if (this.state.status === "FAILED") return "bad";
      return "wait";
    },
    badgeClass() {
      return { ok: "bg-success", bad: "bg-danger", wait: "bg-secondary" }[this.statusKind];
    },
    statusLabel() {
      return {
        PENDING_DNS: "Venter på DNS",
        ISSUING: "Setter opp sikker tilkobling",
        ACTIVE: "Aktiv",
        FAILED: "Feilet"
      }[this.state.status] || this.state.status;
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      SettingsService.getCustomDomain().then(
        (response) => {
          this.state = response.data || {};
          this.host = this.displayHost;
          this.serverAddress = this.state.serverAddress || "";
          this.platformHost = this.state.platformHost || "";
          this.loading = false;
        },
        (error) => { console.log(error); this.loading = false; }
      );
      this.loadVivaSources();
    },
    loadVivaSources() {
      if (!this.isSuperAdmin) return;
      SettingsService.getCustomDomainVivaSources().then(
        (response) => { this.viva = response.data || null; },
        (error) => { console.log(error); this.viva = null; }
      );
    },
    async moveSource(row, replace = false) {
      const name = row.name || row.shortName;
      const ok = await this.$refs.confirmDialogue.show({
        title: "Lag ny VIVA-kilde",
        message: replace
          ? "Kilde " + row.sourceCode + " sender gjesten riktig tilbake til " + this.viva.expectedHost + ". " +
            "Lag en ny bare hvis den avviser betalinger — gjesten kommer tilbake med eventId=12005 og ingen " +
            "transaksjon, typisk etter at kilden er redigert i VIVA self-care. Den nye kilden lages gjennom VIVAs " +
            "API, uten logo, og " + name + " bytter til den med en gang. Den gamle blir liggende ubrukt i VIVA."
          : "Lager en ny betalingskilde hos VIVA for " + name +
            " som sender gjesten tilbake til " + this.viva.expectedHost + ", og bytter til den. " +
            "Den gamle kilden blir liggende ubrukt i VIVA.",
        okButton: "Lag ny kilde",
        cancelButton: "Avbryt"
      });
      if (!ok) return;
      this.moving = row.locationId;
      this.sourceMessage = "";
      this.sourceMessageError = false;
      SettingsService.moveCustomDomainVivaSource(row.locationId, replace).then(
        (response) => {
          const data = response.data || {};
          if (data.sources) this.viva = data.sources;
          this.sourceMessage = (row.name || row.shortName) + ": " + (data.message || "Ny kilde laget.");
          this.moving = "";
        },
        (error) => {
          this.sourceMessageError = true;
          this.sourceMessage = (row.name || row.shortName) + ": " +
            ((error.response && error.response.data && error.response.data.error) || "Kunne ikke lage ny kilde.");
          this.moving = "";
        }
      );
    },
    verdictLabel(verdict) {
      return {
        OK: "Riktig",
        WRONG_DOMAIN: "Feil domene",
        UNKNOWN_DOMAIN: "Ukjent",
        NO_SOURCE: "Mangler kilde",
        NOT_VIVA: "Ikke VIVA"
      }[verdict] || verdict;
    },
    verdictBadge(verdict) {
      return {
        OK: "bg-success",
        WRONG_DOMAIN: "bg-danger",
        NO_SOURCE: "bg-danger",
        UNKNOWN_DOMAIN: "bg-warning text-dark",
        NOT_VIVA: "bg-secondary"
      }[verdict] || "bg-secondary";
    },
    /** What to do about a row, or "" when nothing. */
    verdictHelp(row) {
      const fix = row.canMove ? " Trykk «Lag ny kilde»." : "";
      if (row.verdict === "WRONG_DOMAIN") {
        return "Kilden returnerer til " + row.sourceDomain + ", men gjesten bestiller på " +
          this.viva.expectedHost + "." + fix;
      }
      if (row.verdict === "NO_SOURCE") {
        return "Avdelingen er på VIVA uten betalingskilde: gjesten kommer ikke tilbake til nettsiden etter " +
          "betaling, og bestillingen blir ikke opprettet." +
          (row.canMove ? fix : " VIVA-onboardingen må fullføres først, eller Storefront-domene må fylles inn.");
      }
      if (row.verdict === "UNKNOWN_DOMAIN") {
        return "Kilden ble laget før CAGISTA begynte å registrere domenet på den (8. oktober 2026), og VIVA " +
          "har ikke noe kall som leser den tilbake. Ble VIVA aktivert før " +
          (this.viva.customDomainActive && this.viva.activatedEpoch
            ? "domenet ble aktivt (" + this.formatWhen(this.viva.activatedEpoch) + ")"
            : "et eget domene ble aktivt") +
          ", returnerer den til " + this.viva.platformHost + "." + fix;
      }
      return "";
    },
    save() {
      this.send(this.host);
    },
    async remove() {
      const ok = await this.$refs.confirmDialogue.show({
        title: "Fjern domenet",
        message: "Bestillingssiden blir liggende på " + (this.platformHost || "vår adresse") +
          ". Husk å peke domenet ditt et annet sted — ellers møter gjestene dine en død side.",
        okButton: "Fjern",
        cancelButton: "Avbryt"
      });
      if (ok) this.send("");
    },
    send(host) {
      this.saving = true;
      this.message = "";
      this.messageError = false;
      SettingsService.setCustomDomain(host).then(
        (response) => {
          this.state = response.data || {};
          this.host = this.displayHost;
          this.message = host ? "Lagret. Vi sjekker DNS automatisk." : "Domenet er fjernet.";
          this.saving = false;
          this.loadVivaSources();
        },
        (error) => {
          this.messageError = true;
          this.message = (error.response && error.response.data && error.response.data.error)
            || "Kunne ikke lagre. Prøv igjen.";
          this.saving = false;
        }
      );
    },
    formatWhen(epoch) {
      try { return new Date(epoch).toLocaleString("nb-NO"); } catch (e) { return ""; }
    }
  }
};
</script>

<style scoped>
.cd-card { border: 1px solid var(--bo-line); border-radius: 10px; padding: 1rem 1.1rem; }
.cd-card--ok { border-color: #bfe6c8; background: var(--bo-ok-bg); }
.cd-card--bad { border-color: #f3c9c9; background: var(--bo-bad-bg); }
.cd-card--wait { background: var(--bo-surface-2); }
.cd-note { font-size: 0.82rem; color: var(--bo-muted); line-height: 1.5; }
.cd-dns td { padding: 0.3rem 0.6rem; }
.cd-dns th { padding: 0.3rem 0.6rem; color: var(--bo-muted); font-weight: 600; font-size: 0.8rem; }

/* The SUPER_ADMIN box: set apart from the merchant's own content above it. */
.cd-admin { margin-top: 0.5rem; border-style: dashed; }
.cd-tag {
  font-size: 0.72rem; color: var(--bo-muted); background: var(--bo-surface-2);
  border-radius: 20px; padding: 2px 10px;
}
.cd-cases { padding-left: 1.2rem; }
.cd-cases li + li { margin-top: 0.5rem; }
.cd-warn {
  font-size: 0.85rem; border: 1px solid #f3c9c9; background: var(--bo-bad-bg);
  border-radius: 8px; padding: 0.5rem 0.75rem;
}
.cd-sources { width: 100%; font-size: 0.85rem; }
.cd-sources td, .cd-sources th { padding: 0.35rem 0.5rem; vertical-align: top; }
.cd-sources th { color: var(--bo-muted); font-weight: 600; font-size: 0.8rem; }
.cd-sources td { word-break: break-word; }
.cd-sub { font-size: 0.75rem; color: var(--bo-muted); }
/* Sits under the status badge; narrower than a page button so the table keeps its four columns. */
.cd-move { display: block; min-width: 0; padding: 3px 10px; font-size: 0.8rem; white-space: nowrap; }
</style>
